import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";

// PHASE 8.42 — Helper to read the current cookie-consent state. Returns true
// when the user has explicitly opted in to "analytics" cookies. Single
// source of truth for any analytics/marketing pixel we wire up.
const CONSENT_STORAGE_KEY = "oodel_cookie_consent";
export function hasAnalyticsConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (parsed.choice === "reject_all") return false;
    if (parsed.choice === "accept_all") return true;
    return Array.isArray(parsed.categories) && parsed.categories.includes("analytics");
  } catch {
    return false;
  }
}

/**
 * GAScript — CMS-driven Google Analytics 4 loader.
 *
 * Reads /api/site-content once, then:
 *   - if `ga_enabled !== "true"` → renders nothing.
 *   - if `ga_track_only_on_canonical_host` is true and the current host
 *     doesn't match `seo_canonical_base`'s host → renders nothing (prevents
 *     preview/staging from polluting prod GA).
 *   - else loads https://www.googletagmanager.com/gtag/js?id=<ID> and runs
 *     the standard gtag('config', ID) init exactly as Google specs.
 *
 * PHASE 8.42 — CONSENT GATING: The loader now also waits for the visitor
 * to grant the "analytics" cookie consent category. Before consent is
 * granted, Google Consent Mode v2 is set to DENIED so no event ships.
 * When the visitor accepts analytics (or toggles it on in cookie settings),
 * we fire `consent('update', { analytics_storage: 'granted' })` and the
 * page_view backlog flushes naturally. Revoking analytics flips it back
 * to denied immediately. The CookieConsent component dispatches a window
 * `oodel:consent-updated` CustomEvent so this script doesn't have to
 * poll localStorage.
 *
 * After mount, the component also auto-fires a `page_view` event on every
 * React-Router navigation so SPA route changes show up in GA (without this
 * step, GA would only see the initial document load).
 *
 * Mounted ONCE near the top of <App />.
 */
export default function GAScript() {
  const location = useLocation();
  const configRef = useRef(null); // {id, enabled}
  const initedRef = useRef(false);
  const [consent, setConsent] = useState(hasAnalyticsConsent);

  // Listen for cookie-consent changes so analytics_storage flips live without
  // a page reload. Fires both on banner choices and on the granular toggle.
  useEffect(() => {
    const onChange = () => {
      const next = hasAnalyticsConsent();
      setConsent(next);
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        try {
          window.gtag("consent", "update", { analytics_storage: next ? "granted" : "denied" });
        } catch { /* swallow */ }
      }
    };
    window.addEventListener("oodel:consent-updated", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("oodel:consent-updated", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  // One-time load: fetch site_content and install the gtag.js script tag.
  // When the static snippet in `public/index.html` has already loaded gtag.js
  // (so `window.gtag` exists), we skip the loader install and just take over
  // for SPA route tracking. This keeps Google's tag-detection happy AND
  // gives us per-route page_view events.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/site-content");
        if (cancelled) return;
        const envId = (process.env.REACT_APP_GA4_ID || "").trim();
        // Env var beats CMS — admin can flip via .env without a DB write.
        // CMS still controls anonymize_ip + canonical-host gating.
        const enabled = !!envId || (data.ga_enabled || "").toLowerCase() === "true";
        const id = envId || (data.ga_measurement_id || "").trim();
        const hostGate = (data.ga_track_only_on_canonical_host || "true").toLowerCase() === "true";
        const anonymizeIp = (data.ga_anonymize_ip || "true").toLowerCase() === "true";
        const canonicalBase = data.seo_canonical_base || "";

        // Static-snippet path — gtag.js was already loaded server-side by
        // index.html. Just record that we're inited so route changes track.
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          // CMS-driven "off" switch: deny analytics_storage via Consent Mode,
          // which silences future events without breaking the page.
          if (!enabled || (id && !id.startsWith("G-"))) {
            try { window.gtag("consent", "update", { analytics_storage: "denied" }); } catch { /* swallow */ }
            return;
          }
          // Host gate — even when GA loaded, send no events on preview env.
          if (hostGate && canonicalBase) {
            try {
              const canonicalHost = new URL(canonicalBase).host;
              if (canonicalHost && window.location.host !== canonicalHost) {
                try { window.gtag("consent", "update", { analytics_storage: "denied" }); } catch { /* swallow */ }
                return;
              }
            } catch { /* bad canonical url, ignore */ }
          }
          // Optional anonymisation refresh — safe to call after the static
          // snippet's config; gtag merges parameters.
          try { window.gtag("config", id, { anonymize_ip: anonymizeIp, send_page_view: false }); } catch { /* swallow */ }
          try { window.gtag("consent", "update", { analytics_storage: hasAnalyticsConsent() ? "granted" : "denied" }); } catch { /* swallow */ }
          configRef.current = { id, enabled: true, anonymizeIp };
          initedRef.current = true;
          return;
        }

        // No static snippet (or it failed to load) — fall back to dynamic
        // injection from the CMS settings, exactly as before.
        if (!enabled || !id || !id.startsWith("G-")) return;
        if (hostGate && canonicalBase) {
          try {
            const canonicalHost = new URL(canonicalBase).host;
            if (canonicalHost && window.location.host !== canonicalHost) return;
          } catch { /* bad canonical url, ignore */ }
        }
        if (!document.getElementById("gtag-loader")) {
          const s = document.createElement("script");
          s.id = "gtag-loader";
          s.async = true;
          s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
          document.head.appendChild(s);
        }
        window.dataLayer = window.dataLayer || [];
        // eslint-disable-next-line prefer-rest-params
        function gtag() { window.dataLayer.push(arguments); }
        window.gtag = gtag;
        // CONSENT MODE v2 — default everything to DENIED until the visitor
        // explicitly opts in via the cookie banner. This is the recommended
        // default per GDPR/PDPB and is what google.com itself uses.
        gtag("consent", "default", {
          analytics_storage: "denied",
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
          wait_for_update: 500,
        });
        gtag("js", new Date());
        gtag("config", id, { send_page_view: false, anonymize_ip: anonymizeIp });
        // Flip to granted if the visitor already chose analytics on a
        // previous visit. Otherwise the banner click will flip it later.
        if (hasAnalyticsConsent()) {
          gtag("consent", "update", { analytics_storage: "granted" });
        }
        configRef.current = { id, enabled, anonymizeIp };
        initedRef.current = true;
        gtag("event", "page_view", {
          page_title: document.title,
          page_path: location.pathname + location.search,
          page_location: window.location.href,
        });
      } catch {
        // GA failure must never break the app — swallow silently.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent]);

  // SPA route change → fire a fresh page_view. We rely on initedRef so the
  // first navigation after init doesn't double-fire.
  const lastPathRef = useRef(null);
  useEffect(() => {
    if (!initedRef.current || !window.gtag || !configRef.current) return;
    const path = location.pathname + location.search;
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;
    // Title might still be the previous one if the route hasn't rendered yet —
    // give the DOM one tick to settle.
    const handle = setTimeout(() => {
      window.gtag("event", "page_view", {
        page_title: document.title,
        page_path: path,
        page_location: window.location.href,
      });
    }, 50);
    return () => clearTimeout(handle);
  }, [location.pathname, location.search]);

  return null;
}

/**
 * Tiny helper to fire a custom GA event from anywhere. Silently no-ops when
 * GA hasn't loaded (e.g. admin turned it off, or visitor blocked the script).
 */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined" || !window.gtag) return;
  try { window.gtag("event", name, params); } catch { /* swallow */ }
}
